import React, { useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../contexts/NotificationContext';
import { NotificationCategory } from '../../types/notification';
import './NotificationCenter.css';

type FilterType = 'all' | 'expense' | 'nomination' | 'performance' | 'timesheet' | 'unread';

const NotificationCenter: React.FC = () => {
  const {
    notifications,
    unreadCount,
    isOpen,
    togglePanel,
    closePanel,
    markAsRead,
    markAllAsRead,
    deleteOne,
    clearAll,
  } = useNotifications();
  const navigate = useNavigate();
  const panelRef = useRef<HTMLDivElement>(null);
  const bellRef = useRef<HTMLButtonElement>(null);
  const [filter, setFilter] = useState<FilterType>('all');

  // Listen for external toggle events (e.g. from Sidebar)
  useEffect(() => {
    const handleToggle = () => togglePanel();
    window.addEventListener('toggleNotificationPanel', handleToggle);
    return () => window.removeEventListener('toggleNotificationPanel', handleToggle);
  }, [togglePanel]);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (
        isOpen &&
        panelRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        bellRef.current &&
        !bellRef.current.contains(e.target as Node)
      ) {
        closePanel();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen, closePanel]);

  // Close on Escape
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) closePanel();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, closePanel]);

  const filtered = notifications.filter((n) => {
    if (filter === 'unread') return !n.read;
    if (filter === 'expense') return n.category === NotificationCategory.EXPENSE;
    if (filter === 'nomination') return n.category === NotificationCategory.NOMINATION;
    if (filter === 'performance') return n.category === NotificationCategory.PERFORMANCE;
    if (filter === 'timesheet') return n.category === NotificationCategory.TIMESHEET;
    return true;
  });

  const handleNotificationClick = (id: string, link?: string) => {
    markAsRead(id);
    if (link) {
      closePanel();
      navigate(link);
    }
  };

  const timeAgo = (dateStr: string): string => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  };

  return (
    <>
      {/* Bell Icon - Fixed position top-right */}
      <button
        ref={bellRef}
        className={`notification-bell ${isOpen ? 'active' : ''}`}
        onClick={togglePanel}
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unreadCount > 0 && (
          <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>

      {/* Overlay */}
      {isOpen && <div className="notification-overlay" onClick={closePanel} />}

      {/* Slide-out Panel */}
      <div ref={panelRef} className={`notification-panel ${isOpen ? 'open' : ''}`}>
        {/* Panel Header */}
        <div className="notif-panel-header">
          <div className="notif-panel-title-row">
            <h2 className="notif-panel-title">Notifications</h2>
            {unreadCount > 0 && <span className="notif-unread-pill">{unreadCount} new</span>}
          </div>
          <div className="notif-panel-actions">
            {unreadCount > 0 && (
              <button className="notif-action-btn" onClick={markAllAsRead} title="Mark all as read">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 11 12 14 22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg>
              </button>
            )}
            {notifications.length > 0 && (
              <button className="notif-action-btn notif-clear-btn" onClick={clearAll} title="Clear all">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
              </button>
            )}
            <button className="notif-close-btn" onClick={closePanel} aria-label="Close notifications">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
            </button>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="notif-filters">
          {(['all', 'unread', 'expense', 'nomination', 'performance', 'timesheet'] as FilterType[]).map((f) => (
            <button
              key={f}
              className={`notif-filter-tab ${filter === f ? 'active' : ''}`}
              onClick={() => setFilter(f)}
            >
              {f === 'all' && 'All'}
              {f === 'unread' && 'Unread'}
              {f === 'expense' && '💰 Expenses'}
              {f === 'nomination' && '⭐ Nominations'}
              {f === 'performance' && '🎯 Performance'}
              {f === 'timesheet' && '⏰ Timesheets'}
            </button>
          ))}
        </div>

        {/* Notification List */}
        <div className="notif-list">
          {filtered.length === 0 ? (
            <div className="notif-empty">
              <div className="notif-empty-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
              </div>
              <p className="notif-empty-text">
                {filter === 'unread' ? 'All caught up!' : 'No notifications yet'}
              </p>
              <span className="notif-empty-sub">
                {filter === 'unread'
                  ? 'You have no unread notifications'
                  : 'Notifications will appear here when actions happen'}
              </span>
            </div>
          ) : (
            filtered.map((notif) => (
              <div
                key={notif.id}
                className={`notif-item ${!notif.read ? 'unread' : ''}`}
                onClick={() => handleNotificationClick(notif.id, notif.link)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') handleNotificationClick(notif.id, notif.link); }}
              >
                <div className="notif-item-icon">{notif.icon}</div>
                <div className="notif-item-body">
                  <div className="notif-item-title">{notif.title}</div>
                  <div className="notif-item-message">{notif.message}</div>
                  <div className="notif-item-time">{timeAgo(notif.createdAt)}</div>
                </div>
                {!notif.read && <div className="notif-item-dot" />}
                <button
                  className="notif-item-delete"
                  onClick={(e) => { e.stopPropagation(); deleteOne(notif.id); }}
                  aria-label="Delete notification"
                  title="Delete"
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
};

export default NotificationCenter;
